import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { mockStats, mockEvents, companyInfo } from '../data/mock';
import {
  ArrowRight,
  Building2,
  Users,
  MapPin,
  Home as HomeIcon,
  Calendar,
  GraduationCap,
  Scale,
  Award,
  CheckCircle2,
  Shield,
  Briefcase,
  Network,
  ChevronRight,
  LayoutDashboard,
  Bot,
  Video,
  BookOpen,
  MessageSquare,
  Cpu,
  Globe
} from 'lucide-react';

// Logos de aliados estratégicos (mock - se gestionarán desde admin)
const aliadosLogos = [
  { id: 1, name: 'Colegio Nacional de Inmobiliarios', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop' },
  { id: 2, name: 'AMPI', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop' },
  { id: 3, name: 'CONOCER', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop' },
  { id: 4, name: 'Mercado Libre', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop' },
  { id: 5, name: 'EasyBroker', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop' },
  { id: 6, name: 'Morgana', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop' },
];

// Logos de proveedores certificados (mock - se gestionarán desde admin)
const proveedoresLogos = [
  { id: 1, name: 'Notaría 125', logo: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=120&h=60&fit=crop' },
  { id: 2, name: 'Avalúos Pro', logo: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=120&h=60&fit=crop' },
  { id: 3, name: 'Créditos Express', logo: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=120&h=60&fit=crop' },
  { id: 4, name: 'Seguros MX', logo: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=120&h=60&fit=crop' },
  { id: 5, name: 'Marketing Digital', logo: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=120&h=60&fit=crop' },
  { id: 6, name: 'Legal Inmobiliario', logo: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=120&h=60&fit=crop' },
];

const HomePage = () => {
  const stats = [
    { label: 'Propiedades en Renta', value: mockStats.propiedadesRenta.toLocaleString(), icon: HomeIcon },
    { label: 'Propiedades en Venta', value: mockStats.propiedadesVenta.toLocaleString(), icon: Building2 },
    { label: 'Afiliados Activos', value: mockStats.afiliados.toLocaleString(), icon: Users },
    { label: 'Estados de la República', value: mockStats.estados, icon: MapPin },
  ];

  // Beneficios principales con links a secciones detalladas
  const mainBenefits = [
    {
      id: 'formacion',
      icon: GraduationCap,
      title: 'Formación Continua',
      description: 'Master Classes quincenales, certificación CONOCER EC0110.02 y acceso a la Academia UPIM.',
    },
    {
      id: 'asesoria',
      icon: Scale,
      title: 'Asesoría Integral',
      description: 'Asesoría jurídica, financiera y contable sin costo adicional para todos los afiliados.',
    },
    {
      id: 'networking',
      icon: Network,
      title: 'Red de Networking',
      description: 'Grupos de WhatsApp por zona geográfica y conexión con más de 2,000 profesionales.',
    },
    {
      id: 'blindaje',
      icon: Shield,
      title: 'Blindaje Jurídico',
      description: 'Contratos modelo NOM-247, protocolos y documentos para operaciones seguras.',
    },
    {
      id: 'descuentos',
      icon: Award,
      title: 'Descuentos Exclusivos',
      description: 'Precios preferenciales en avalúos, notarías, seguros y proveedores certificados.',
    },
    {
      id: 'herramientas',
      icon: Briefcase,
      title: 'Herramientas Profesionales',
      description: 'Biblioteca digital con templates, contratos, formatos y material de capacitación.',
    },
  ];

  const upcomingEvents = mockEvents.slice(0, 2);

  // Carrusel infinito de logos
  const LogoCarousel = ({ logos, direction = 'left' }) => {
    return (
      <div className="overflow-hidden">
        <div className={`flex space-x-12 ${direction === 'left' ? 'animate-scroll-left' : 'animate-scroll-right'}`}>
          {[...logos, ...logos, ...logos].map((logo, index) => (
            <div 
              key={`${logo.id}-${index}`} 
              className="flex-shrink-0 h-16 w-32 bg-white rounded-lg shadow-sm flex items-center justify-center p-3 grayscale hover:grayscale-0 transition-all"
            >
              <span className="text-xs text-gray-600 font-medium text-center">{logo.name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center bg-gradient-to-br from-gray-100 via-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <div className="text-center lg:text-left">
              {/* Logo grande en lugar del texto */}
              <div className="mb-6">
                <img 
                  src="/assets/upim-logo.svg"
                  alt="UPIM - Unión de Profesionales Inmobiliarios de México"
                  className="h-36 lg:h-44 w-auto mx-auto lg:mx-0 object-contain"
                />
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Profesionaliza tu carrera{' '}
                <span className="text-gray-600">inmobiliaria</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Únete a la asociación más tecnológica e innovadora de México. 
                Accede a formación, asesoría, networking y herramientas exclusivas 
                para impulsar tu negocio.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  size="lg" 
                  className="bg-gray-900 hover:bg-gray-800 text-white px-8 h-12 text-base"
                  asChild
                >
                  <Link to="/afiliacion">
                    Conocer Planes
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-gray-300 hover:bg-gray-50 px-8 h-12 text-base"
                  asChild
                >
                  <Link to="/beneficios">
                    Ver Beneficios
                  </Link>
                </Button>
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="relative hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1769736438943-3c57cbd02c22?w=800&h=600&fit=crop"
                  alt="Interior de hogar moderno"
                  className="w-full h-auto object-cover"
                />
              </div>
              
              {/* Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-5 border border-gray-100">
                <div className="flex items-center space-x-4">
                  <div className="h-14 w-14 bg-gray-100 rounded-full flex items-center justify-center">
                    <Users className="h-7 w-7 text-gray-700" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">+{mockStats.afiliados}</p>
                    <p className="text-sm text-gray-500">Profesionales afiliados</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="h-8 w-8 mx-auto mb-3 text-gray-400" />
                <p className="text-3xl lg:text-4xl font-bold mb-1">{stat.value}</p>
                <p className="text-sm text-gray-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section con links */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
              ¿Por qué UPIM?
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Todo lo que necesitas para crecer profesionalmente
            </h2>
            <p className="text-gray-600 text-lg">
              Desde 2016, impulsamos la profesionalización del sector inmobiliario en México 
              con beneficios diseñados para tu éxito.
            </p>
          </div>
          
          {/* Benefits Grid - Con links */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainBenefits.map((benefit, index) => (
              <Link 
                key={index}
                to={`/beneficios/${benefit.id}`}
                className="p-6 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 group block"
              >
                <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center mb-4 group-hover:bg-gray-900 transition-colors">
                  <benefit.icon className="h-6 w-6 text-gray-700 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">{benefit.description}</p>
                <span className="text-gray-700 text-sm font-medium inline-flex items-center group-hover:text-gray-900">
                  Conocer más <ChevronRight className="ml-1 h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button 
              size="lg" 
              variant="outline"
              className="border-gray-300 hover:bg-gray-50"
              asChild
            >
              <Link to="/beneficios">
                Ver todos los beneficios
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Plataforma Intranet + CRM + IA */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
                Plataforma Exclusiva
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Tu plataforma exclusiva como socio UPIM con CRM e inteligencia artificial
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Como afiliado UPIM accedes a una plataforma completa diseñada para potenciar 
                tu negocio inmobiliario con tecnología de vanguardia.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-4 p-4 bg-white rounded-lg border border-gray-100">
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <LayoutDashboard className="h-5 w-5 text-gray-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Dashboard Personalizado</h4>
                    <p className="text-sm text-gray-600">Gestiona tu membresía, eventos, documentos y beneficios desde un solo lugar.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-white rounded-lg border border-gray-100">
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-5 w-5 text-gray-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">CRM con Funciones IA</h4>
                    <p className="text-sm text-gray-600">Próximamente: gestiona tus contactos, leads y operaciones con asistencia de inteligencia artificial.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-white rounded-lg border border-gray-100">
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Globe className="h-5 w-5 text-gray-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Bolsa Inmobiliaria Unificada</h4>
                    <p className="text-sm text-gray-600">Conecta tu inventario con la red de afiliados y accede a propiedades de toda la República.</p>
                  </div>
                </div>
              </div>
              
              <Button asChild className="bg-gray-900 hover:bg-gray-800">
                <Link to="/login">
                  Acceder a mi cuenta
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            
            <div className="relative">
              <img
                src="/assets/event-banner.png"
                alt="Plataforma UPIM con IA"
                className="rounded-2xl shadow-lg w-full object-contain bg-gray-100"
                style={{ minHeight: '400px', maxHeight: '600px' }}
              />
              <div className="absolute -bottom-6 -right-6 bg-gray-900 text-white rounded-xl p-5 shadow-xl hidden md:block">
                <div className="flex items-center space-x-3">
                  <Cpu className="h-8 w-8 text-gray-400" />
                  <div>
                    <p className="font-bold">UPIM IA</p>
                    <p className="text-sm text-gray-400">Altitud Inmobiliaria</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Academia UPIM */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <img
                src="https://images.unsplash.com/photo-1762176263996-a0713a49ee4d?w=700&h=500&fit=crop"
                alt="Academia UPIM - Sala de capacitación corporativa"
                className="rounded-2xl shadow-lg w-full"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
                Academia UPIM
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Capacitación continua con tu mismo usuario y contraseña
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Accede a todas las capacitaciones con las credenciales de tu cuenta UPIM. 
                Sin registros adicionales, sin complicaciones.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Master Classes semanales gratuitas</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Webinars exclusivos de UPIM</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Cursos del Colegio Nacional de Inmobiliarios</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Descuentos especiales en certificaciones</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Biblioteca de grabaciones disponible 24/7</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Button asChild className="bg-gray-900 hover:bg-gray-800">
                  <Link to="/academia">
                    <Video className="mr-2 h-4 w-4" />
                    Explorar Academia
                  </Link>
                </Button>
                <Button asChild variant="outline" className="border-gray-300">
                  <Link to="/calendario">
                    <Calendar className="mr-2 h-4 w-4" />
                    Ver Calendario
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
                Agenda
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                Próximos eventos
              </h2>
              <p className="text-gray-600 max-w-xl">
                Participa en nuestras capacitaciones, talleres y eventos de networking.
              </p>
            </div>
            <Button asChild variant="outline" className="mt-4 md:mt-0 border-gray-300">
              <Link to="/calendario">
                Ver calendario completo
                <Calendar className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {upcomingEvents.map((event) => (
              <Card 
                key={event.id} 
                className="overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="grid md:grid-cols-5">
                  <div className="md:col-span-2 h-48 md:h-full overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="md:col-span-3 p-6 flex flex-col justify-center">
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <Calendar className="h-4 w-4 mr-2" />
                      {new Date(event.date).toLocaleDateString('es-MX', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                      <span className="mx-2">•</span>
                      {event.time} hrs
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>
                    <Button size="sm" className="w-fit bg-gray-900 hover:bg-gray-800">
                      Más información
                    </Button>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Alianzas Estratégicas */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              Alianzas Estratégicas
            </h2>
            <p className="text-gray-600">Trabajamos con las mejores instituciones del sector</p>
          </div>
          
          <div className="relative overflow-hidden">
            <div className="flex space-x-12 animate-marquee">
              {[...aliadosLogos, ...aliadosLogos].map((logo, index) => (
                <div 
                  key={`aliado-${logo.id}-${index}`} 
                  className="flex-shrink-0 h-20 w-40 bg-gray-50 rounded-lg flex items-center justify-center p-4 grayscale hover:grayscale-0 transition-all border border-gray-100"
                >
                  <span className="text-sm text-gray-600 font-medium text-center">{logo.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Proveedores Certificados */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              Proveedores Certificados
            </h2>
            <p className="text-gray-600">Descuentos exclusivos para afiliados UPIM</p>
          </div>
          
          <div className="relative overflow-hidden">
            <div className="flex space-x-12 animate-marquee-reverse">
              {[...proveedoresLogos, ...proveedoresLogos].map((logo, index) => (
                <div 
                  key={`proveedor-${logo.id}-${index}`} 
                  className="flex-shrink-0 h-20 w-40 bg-white rounded-lg flex items-center justify-center p-4 grayscale hover:grayscale-0 transition-all border border-gray-100 shadow-sm"
                >
                  <span className="text-sm text-gray-600 font-medium text-center">{logo.name}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Button asChild variant="outline" className="border-gray-300">
              <Link to="/proveedores">
                Ver todos los proveedores
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Impulsa tu carrera inmobiliaria hoy
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Únete a más de {mockStats.afiliados.toLocaleString()} profesionales que ya 
            disfrutan de los beneficios de ser parte de UPIM.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-gray-900 hover:bg-gray-100 px-8 h-12"
              asChild
            >
              <Link to="/afiliacion">
                Afíliate por $3,500/año
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-gray-600 text-white hover:bg-gray-800 px-8 h-12"
              asChild
            >
              <Link to="/contacto">
                Contactar a UPIM
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
